// Modernist primitives: flat, zero-radius, flush-left labels, hairline rules.
// Styled with the CSS variables from tokens.css so themes swap for free.

import React from "react";

export const Rule = ({ strong = false, style }) => (
  <div style={{ height: strong ? 2 : 1, background: strong ? "var(--ink)" : "var(--rule)", flex: "none", ...style }} />
);

export const Kicker = ({ children, tone = "accent", style }) => (
  <div style={{
    font: "600 10px/1 var(--bq-font-display)",
    letterSpacing: ".14em",
    textTransform: "uppercase",
    color: tone === "accent" ? "var(--accent)" : "var(--muted)",
    ...style,
  }}>{children}</div>
);

export const Display = ({ children, size = 30, style }) => (
  <h2 style={{
    margin: 0,
    font: "800 " + size + "px/0.95 var(--bq-font-display)",
    letterSpacing: "-0.02em",
    color: "var(--ink)",
    ...style,
  }}>{children}</h2>
);

/** Labels are flush left even when the button is wider than its text. */
export function Button({ variant = "primary", children, style, disabled, ...rest }) {
  const base = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    border: "none",
    padding: "13px 15px",
    minHeight: 46,
    font: "700 11px/1 var(--bq-font-display)",
    letterSpacing: ".1em",
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.45 : 1,
  };
  const variants = {
    primary: { background: "var(--accent)", color: "var(--onAccent)" },
    secondary: { background: "transparent", color: "var(--ink)", border: "1px solid var(--ink)" },
    ghost: { background: "transparent", color: "var(--muted2)", padding: "8px 2px", minHeight: 36, letterSpacing: ".1em" },
  };
  return <button disabled={disabled} style={{ ...base, ...variants[variant], ...style }} {...rest}>{children}</button>;
}

export function IconButton({ label, active = false, children, style, ...rest }) {
  return (
    <button
      aria-label={label}
      style={{
        width: 44, height: 44, flex: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "none",
        background: active ? "var(--accent)" : "transparent",
        color: active ? "var(--onAccent)" : "var(--ink)",
        cursor: "pointer",
        ...style,
      }}
      {...rest}
    >{children}</button>
  );
}

export function Badge({ tone = "outline", children, style }) {
  const tones = {
    solid: { background: "var(--ink)", color: "var(--bg)", border: "none", padding: "4px 7px" },
    outline: { background: "transparent", color: "var(--accentDeep)", border: "1px solid var(--accent)", padding: "3px 6px" },
    muted: { background: "transparent", color: "var(--muted)", border: "1px solid var(--rule2)", padding: "3px 6px" },
  };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, flex: "none",
      font: "600 9px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase",
      ...tones[tone], ...style,
    }}>{children}</span>
  );
}

export function Field({ label, hint, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, ...style }}>
      {label && (
        <label style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted)" }}>
          {label}
        </label>
      )}
      {children}
      {hint && <span style={{ font: "400 11px/1.4 var(--bq-font-body)", color: "var(--muted)" }}>{hint}</span>}
    </div>
  );
}

export const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid var(--ink)",
  outline: "none",
  background: "var(--bg)",
  color: "var(--ink)",
  padding: 12,
  font: "400 14px/1.3 var(--bq-font-body)",
};

export function Segmented({ options, value, onChange, style }) {
  return (
    <div style={{ display: "flex", border: "1px solid var(--rule2)", flex: "none", ...style }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            onClick={() => !active && onChange(opt.value)}
            style={{
              border: "none",
              background: active ? "var(--ink)" : "transparent",
              color: active ? "var(--bg)" : "var(--muted)",
              padding: "6px 12px",
              font: "600 10px/1 var(--bq-font-display)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              cursor: active ? "default" : "pointer",
            }}
          >{opt.label}</button>
        );
      })}
    </div>
  );
}

export function Switch({ on, onChange, label }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onChange}
      style={{
        flex: "none", width: 52, height: 30, padding: 2,
        border: on ? "1px solid var(--ink)" : "1px solid var(--rule2)",
        background: on ? "var(--accent)" : "transparent",
        display: "flex", alignItems: "center",
        justifyContent: on ? "flex-end" : "flex-start",
        cursor: "pointer",
      }}
    >
      <span style={{ width: 22, height: 22, display: "block", background: on ? "var(--bg)" : "var(--rule2)" }} />
    </button>
  );
}

export const Progress = ({ pct }) => (
  <div style={{ height: 4, background: "var(--rule)" }}>
    <div style={{ height: 4, background: "var(--accent)", width: Math.max(0, Math.min(100, pct)) + "%", transition: "width .2s linear" }} />
  </div>
);

/** Timestamped request log — the UploadConsole / chat debug console. */
export function Console({ title, endpoint, lines, open, onToggle, maxHeight = 180 }) {
  const color = { req: "var(--accentSoft)", ok: "var(--bg)", err: "#ff6b5a", info: "var(--muted2)" };
  return (
    <div style={{ display: "flex", flexDirection: "column", border: "1px solid var(--ink)" }}>
      <button
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          width: "100%", textAlign: "left", border: "none",
          background: "var(--ink)", color: "var(--bg)", padding: "10px 12px", minHeight: 40, cursor: "pointer",
        }}
      >
        <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase" }}>{title}</span>
        {endpoint && (
          <span style={{
            font: "400 9.5px/1 var(--bq-font-mono)", color: "var(--muted3)",
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right",
          }}>{endpoint}</span>
        )}
      </button>
      {open && (
        <div style={{ background: "var(--console)", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 5, maxHeight, overflow: "auto" }}>
          {lines.length === 0 && (
            <span style={{ font: "400 10.5px/1.4 var(--bq-font-mono)", color: "var(--muted3)" }}>No requests yet.</span>
          )}
          {lines.map((l) => (
            <div key={l.id} style={{ display: "flex", gap: 9 }}>
              <span style={{ flex: "none", font: "400 10.5px/1.4 var(--bq-font-mono)", color: "var(--muted3)" }}>{l.at}</span>
              <span style={{
                flex: 1, minWidth: 0, wordBreak: "break-all",
                font: (l.kind === "req" ? "500 " : "400 ") + "10.5px/1.4 var(--bq-font-mono)",
                color: color[l.kind] || color.info,
              }}>{l.line}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Full-width panel that slides down from under the app header. */
export function SlideOver({ open, offset = 0, onDismiss, children, zIndex = 88, fill = false }) {
  if (!open) return null;
  return (
    <div style={{ position: "absolute", inset: 0, zIndex, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ flex: "none", height: offset, pointerEvents: "none" }} />
      <div style={{
        flex: fill ? 1 : "none",
        minHeight: 0,
        background: "var(--bg)",
        borderTop: "1px solid var(--ink)",
        display: "flex",
        flexDirection: "column",
        animation: "bq-slide .24s cubic-bezier(.2,.7,.3,1) both",
      }}>{children}</div>
      {!fill && (
        <button
          aria-label="Dismiss"
          onClick={onDismiss}
          style={{ flex: 1, border: "none", background: "var(--scrim)", cursor: "pointer", animation: "bq-fade .24s ease both" }}
        />
      )}
    </div>
  );
}

export function Toast({ text }) {
  if (!text) return null;
  return (
    <div style={{
      position: "absolute", left: 20, right: 20, bottom: 92, zIndex: 95,
      background: "var(--ink)", color: "var(--bg)", padding: "12px 14px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{ font: "500 12.5px/1.35 var(--bq-font-body)" }}>{text}</span>
    </div>
  );
}

export function useToast() {
  const [text, setText] = React.useState("");
  const timer = React.useRef(null);
  const flash = React.useCallback((msg, ms = 2600) => {
    clearTimeout(timer.current);
    setText(msg);
    timer.current = setTimeout(() => setText(""), ms);
  }, []);
  React.useEffect(() => () => clearTimeout(timer.current), []);
  return { toast: text, flash };
}
